import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, ShieldCheck, Heart } from 'lucide-react';
import { useSettingsStore } from '../store/settingsStore';
import logo from '../assets/logo.webp';
import deliveryVan from '../assets/delivery_van.png';

const shortLinkCoordinates: Record<string, string> = {
  '39EHNayaQo7ELucK6': '5.965125,80.5480559',
  'fCzZWbvGZLZRz3kT7': '5.965125,80.5480559'
};

const getEmbedMapUrl = (url: string): string => {
  if (!url) {
    return "https://maps.google.com/maps?q=Matara,%20Sri%20Lanka&t=&z=13&ie=UTF8&iwloc=&output=embed";
  }
  
  const trimmed = url.trim();
  
  // If it's already an embed link, return as is
  if (trimmed.includes('output=embed') || trimmed.includes('/maps/embed')) {
    return trimmed;
  }
  
  // Ensure it starts with http/https if it looks like a URL/domain
  let cleanUrl = trimmed;
  if (!/^https?:\/\//i.test(cleanUrl) && (cleanUrl.includes('.') || cleanUrl.includes('/'))) {
    cleanUrl = 'https://' + cleanUrl;
  }

  // Handle shortened Google Maps URLs
  if (cleanUrl.includes('goo.gl') || cleanUrl.includes('maps.app.goo.gl')) {
    const match = cleanUrl.match(/\/([^/?#]+)(?:[?#]|$)/);
    const shortId = match ? match[1] : '';
    const mappedCoords = shortLinkCoordinates[shortId];
    if (mappedCoords) {
      return `https://maps.google.com/maps?q=${mappedCoords}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
    }
    return `https://maps.google.com/maps?q=${encodeURIComponent(cleanUrl)}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
  }

  try {
    const urlObj = new URL(cleanUrl);
    
    // Check if it's a Google Maps URL
    if (urlObj.hostname.includes('google.com') && urlObj.pathname.includes('/maps')) {
      // 1. Try to extract query parameter q if it exists
      const q = urlObj.searchParams.get('q');
      if (q) {
        return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
      }
      
      // 2. Try to extract coordinates from @lat,lng first (more reliable)
      const coordMatch = urlObj.pathname.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        return `https://maps.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
      }
      
      // 3. Try to extract place name from pathname: e.g. /maps/place/Query/
      const placeMatch = urlObj.pathname.match(/\/maps\/place\/([^/]+)/);
      if (placeMatch && placeMatch[1]) {
        const query = decodeURIComponent(placeMatch[1]).replace(/\+/g, ' ');
        return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
      }
    }
  } catch (e) {
    console.error("Failed to parse map URL", e);
  }
  
  // Fallback: Use the query directly
  return `https://maps.google.com/maps?q=${encodeURIComponent(trimmed)}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
};

export const Footer: React.FC = () => {
  const whatsappNumber = useSettingsStore((state) => state.whatsappNumber);
  const email = useSettingsStore((state) => state.contactEmail);
  const address = useSettingsStore((state) => state.contactAddress);
  const socialLinks = useSettingsStore((state) => state.socialLinks);

  return (
    <footer className="bg-slate-950 text-slate-200 font-sans border-t border-slate-900 pt-8 no-print relative overflow-hidden dark-tech-bg">
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 mb-12 px-4 md:px-8">
        {/* Company Intro */}
        <div className="flex flex-col gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="SensorZone.lk Logo" width="36" height="36" className="w-9 h-9 object-contain rounded-full shadow-sm border border-slate-800" />
            <span className="font-extrabold text-xl tracking-tight text-white">
              SensorZone<span className="text-secondary">.lk</span>
            </span>
          </Link>
          <p className="text-sm text-slate-400 leading-relaxed">
            Sri Lanka's premium engineering partner for electronic components, sensors, modules, and IoT systems. Supporting developers, students, and automation professionals nationwide.
          </p>
          <div className="flex flex-col gap-2 mt-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Follow Us</span>
            <div className="flex flex-wrap items-center gap-2">
              {socialLinks?.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 hover:bg-primary hover:text-white hover:border-primary transition-all text-slate-400 hover:text-white flex items-center justify-center" title="Follow us on Facebook" aria-label="Facebook"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z" />
                  </svg>
                </a>
              )}
              {socialLinks?.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 hover:bg-primary hover:text-white hover:border-primary transition-all text-slate-400 hover:text-white flex items-center justify-center" title="Follow us on Instagram" aria-label="Instagram"
                >
                  <svg className="w-4 h-4 fill-none stroke-current" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
              )}
              {socialLinks?.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 hover:bg-primary hover:text-white hover:border-primary transition-all text-slate-400 hover:text-white flex items-center justify-center" title="Follow us on TikTok" aria-label="TikTok"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.02 1.59 4.23.85 1.07 2.01 1.81 3.32 2.16v3.91c-1.24-.16-2.44-.73-3.41-1.55-.71-.6-1.28-1.37-1.64-2.24v8.11c0 4.88-3.95 8.87-8.83 8.87-4.14 0-7.66-2.86-8.56-6.9C-.4 11.97 1.63 7.2 6.01 6.13c1.01-.25 2.06-.21 3.04.1v3.86c-.74-.29-1.55-.38-2.31-.22-1.74.34-2.94 1.94-2.83 3.7.12 1.83 1.64 3.22 3.47 3.12 1.68-.09 3.01-1.47 3.01-3.15V.02z" />
                  </svg>
                </a>
              )}
              {socialLinks?.telegram && (
                <a href={socialLinks.telegram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 hover:bg-primary hover:text-white hover:border-primary transition-all text-slate-400 hover:text-white flex items-center justify-center" title="Follow us on Telegram" aria-label="Telegram"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.24-5.54 3.66-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.46-.42-1.4-.88.03-.24.36-.49.99-.75 3.87-1.68 6.45-2.79 7.74-3.32 3.68-1.51 4.44-1.77 4.94-1.78.11 0 .35.03.5.15.13.1.17.24.19.34.02.13.02.27.01.41z" />
                  </svg>
                </a>
              )}
              {socialLinks?.linkedin && (
                <a href={socialLinks.linkedin} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 hover:bg-primary hover:text-white hover:border-primary transition-all text-slate-400 hover:text-white flex items-center justify-center" title="Follow us on LinkedIn" aria-label="LinkedIn"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Customer Service Column */}
        <div>
          <h2 className="font-extrabold text-white text-base mb-4 tracking-wide uppercase text-sm border-l-2 border-secondary pl-2">Customer Service</h2>
          <ul className="space-y-3.5 text-sm text-slate-400">
            <li>
              <strong className="text-slate-200 block text-xs uppercase tracking-wider">Shipping & Delivery</strong>
              <span className="block mt-0.5 text-xs">Islandwide delivery in 1-3 business days via major couriers.</span>
            </li>
            <li>
              <strong className="text-slate-200 block text-xs uppercase tracking-wider">100% Tested Genuine</strong>
              <span className="block mt-0.5 text-xs">Every component is tested for performance and quality assurance.</span>
            </li>
          </ul>
        </div>

        {/* Contact Info & Quick Links */}
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="font-extrabold text-white text-base mb-4 tracking-wide uppercase text-sm border-l-2 border-secondary pl-2">Get in Touch</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                <span>{address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-secondary shrink-0" />
                <a href={`https://wa.me/${whatsappNumber.replace(/^\+/, '')}`} target="_blank" rel="noreferrer" className="hover:underline hover:text-accent">
                  {(() => {
                    const cleanNum = whatsappNumber.replace(/^\+/, '');
                    return `+${cleanNum.slice(0, 2)} ${cleanNum.slice(2, 4)} ${cleanNum.slice(4, 7)} ${cleanNum.slice(7)}`;
                  })()}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-secondary shrink-0" />
                <a href={`mailto:${email}`} className="hover:underline hover:text-accent">
                  {email}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <span className="font-semibold text-slate-400 text-xs uppercase tracking-wider block mb-2">Quick Links</span>
            <div className="flex gap-4 text-xs text-slate-400">
              <Link to="/wholesale" className="hover:text-accent hover:underline transition-colors">Wholesale</Link>
              <Link to="/account" className="hover:text-accent hover:underline transition-colors">My Profile</Link>
              <Link to="/account?tab=orders" className="hover:text-accent hover:underline transition-colors">Track Orders</Link>
            </div>
          </div>
          <div className="mt-2">
            <span className="font-semibold text-slate-400 text-xs uppercase tracking-wider block mb-2">Popular Categories</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-400">
              <Link to="/category/arduino" className="hover:text-accent hover:underline transition-colors">Arduino</Link>
              <Link to="/category/sensors" className="hover:text-accent hover:underline transition-colors">Sensors</Link>
              <Link to="/category/motors-robotics" className="hover:text-accent hover:underline transition-colors">Motors & Robotics</Link>
              <Link to="/category/logic-analyzers" className="hover:text-accent hover:underline transition-colors">Logic Analyzers</Link>
              <Link to="/category/lights-led" className="hover:text-accent hover:underline transition-colors">Lights & LEDs</Link>
              <Link to="/category/servo-motors" className="hover:text-accent hover:underline transition-colors">Servo Motors</Link>
            </div>
          </div>
        </div>

        {/* Location Map */}
        <div>
          <h2 className="font-extrabold text-white text-base mb-4 tracking-wide uppercase text-sm border-l-2 border-secondary pl-2">Our Location</h2>
          <div className="w-full h-[150px] rounded-xl overflow-hidden border border-slate-800 shadow-md">
            <iframe
              src={getEmbedMapUrl(import.meta.env.VITE_LOCATION_MAP_URL || "")}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              title="SensorZone.lk Location Map"
            ></iframe>
          </div>
        </div>
      </div>

      <div className="divider bg-slate-900 h-[1px] my-6 mx-4 md:mx-8"></div>

      {/* Under footer */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-400 px-4 md:px-8 pb-6">
        <div>
          &copy; {new Date().getFullYear()} SensorZone.lk. All Rights Reserved. Designed for innovators.
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-4 h-4 text-success" /> 100% Genuine Components
          </span>
          <span className="flex items-center gap-1">
            Made with <Heart className="w-3.5 h-3.5 text-red-600 fill-red-500" /> in Sri Lanka
          </span>
        </div>
      </div>

      {/* Animated Delivery Road Ticker */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border-y border-slate-900/80 h-9 relative overflow-hidden w-full flex items-center">
        {/* Road markings */}
        <div className="absolute bottom-[2px] left-0 right-0 h-[1px] border-b border-dashed border-slate-800/60"></div>

        {/* Road ticker texts */}
        <div className="absolute inset-0 flex items-center justify-center gap-16 pointer-events-none">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary shadow-sm shadow-secondary"></span>
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-400">Island-wide Delivery (1-3 Days)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary shadow-sm shadow-secondary"></span>
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-400">Anytime Customer Support</span>
          </div>
        </div>

        {/* Moving Van */}
        <div className="absolute bottom-[3px] w-[56px] h-[24px] animate-drive-van">
          <div className="relative w-full h-full animate-drive-bounce">
            <img src={deliveryVan} alt="Delivery Van" width="56" height="24" className="w-full h-full object-contain" />

            {/* Front Wheel */}
            <div
              className="absolute animate-spin-wheels"
              style={{
                left: '89.32%',
                top: '87.0%',
                width: '24.39%',
                height: '24.39%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full text-slate-800 fill-current">
                <circle cx="50" cy="50" r="45" stroke="#cbd5e1" strokeWidth="10" fill="none" />
                <line x1="50" y1="5" x2="50" y2="95" stroke="#cbd5e1" strokeWidth="10" />
                <line x1="5" y1="50" x2="95" y2="50" stroke="#cbd5e1" strokeWidth="10" />
                <circle cx="50" cy="50" r="15" fill="#475569" />
              </svg>
            </div>

            {/* Back Wheel */}
            <div
              className="absolute animate-spin-wheels"
              style={{
                left: '48.66%',
                top: '87.0%',
                width: '24.39%',
                height: '24.39%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full text-slate-800 fill-current">
                <circle cx="50" cy="50" r="45" stroke="#cbd5e1" strokeWidth="10" fill="none" />
                <line x1="50" y1="5" x2="50" y2="95" stroke="#cbd5e1" strokeWidth="10" />
                <line x1="5" y1="50" x2="95" y2="50" stroke="#cbd5e1" strokeWidth="10" />
                <circle cx="50" cy="50" r="15" fill="#475569" />
              </svg>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
};
